
import React from 'react';
import { UserProfile, Client } from '@/shared/types';

interface TemplateProps {
  profile: UserProfile;
  client?: Client | null;
  title: string;
  metaData: { label: string; value: string }[];
  children: React.ReactNode;
  footerNote?: string;
  type: string;
  containerClass: string;
  LogoImage: React.FC<{ size: 'sm' | 'md' | 'lg' | 'xl'; className?: string; light?: boolean }>;
  hideHeader?: boolean;
}

const CreativeTemplate: React.FC<TemplateProps> = ({
  profile, client, title, metaData, children, footerNote, type, containerClass, LogoImage, hideHeader = false
}) => {
  return (
    <div className={containerClass}>
       {!hideHeader ? (
         <div className="flex h-full">
             {/* Sidebar - Bold Color Block */}
             <div className="w-[35%] h-full p-8 flex flex-col justify-between relative overflow-hidden text-white" style={{ backgroundColor: profile.brandColor }}>
                 {/* Abstract Shapes */}
                 <div className="absolute top-[-50px] right-[-50px] w-48 h-48 rounded-full bg-white/20 blur-3xl"></div>
                 <div className="absolute bottom-[10%] left-[-20%] w-64 h-64 rounded-full bg-black/10 blur-2xl"></div>

                 <div className="relative z-10">
                     <div className="mb-12">
                         <LogoImage size="lg" light className="mb-6 bg-white/10 backdrop-blur-md p-4 rounded-xl" />
                         <h1 className="text-3xl font-black uppercase tracking-tight leading-none mb-2">{profile.name}</h1>
                         <p className="opacity-80 text-sm font-medium">{profile.address}</p>
                     </div>

                     <div className="space-y-6">
                         <div>
                             <p className="text-xs font-bold uppercase tracking-widest opacity-50 mb-1">Contact</p>
                             <p className="text-sm font-medium">{profile.email}</p>
                             <p className="text-sm font-medium">{profile.phone}</p>
                         </div>

                         {client && (
                             <div className="pt-6 border-t border-white/20">
                                 <p className="text-xs font-bold uppercase tracking-widest opacity-50 mb-2">Prepared For</p>
                                 <p className="text-xl font-bold leading-tight mb-1">{client.name}</p>
                                 <p className="opacity-80 text-sm">{client.company}</p>
                                 <p className="opacity-70 text-sm mt-2">{client.address}</p>
                             </div>
                         )}
                     </div>
                 </div>

                 <div className="relative z-10 text-xs opacity-60 font-bold uppercase tracking-widest">
                     Studio / Agency / Independent
                 </div>
             </div>

             {/* Main Content - Clean White */}
             <div className="flex-1 p-12 flex flex-col">
                 <div className="flex justify-between items-end mb-16 border-b-4 border-black pb-6">
                     <div>
                         <h2 className="text-6xl font-black text-neutral-900 uppercase tracking-tighter leading-none">{type}</h2>
                         <p className="text-neutral-500 font-bold uppercase tracking-wider mt-2">{title}</p>
                     </div>
                     <div className="text-right">
                          {metaData.map((m, i) => (
                              <div key={i} className="mb-1">
                                  <span className="text-xs font-bold text-neutral-400 uppercase mr-2">{m.label}</span>
                                  <span className="text-lg font-bold text-neutral-900">{m.value}</span>
                              </div>
                          ))}
                     </div>
                 </div>

                 <div className="flex-1 creative-table-container">
                     {children}
                 </div>

                 <div className="mt-auto pt-8">
                     <div className="bg-neutral-50 p-6 border-l-4" style={{ borderColor: profile.brandColor }}>
                         <p className="font-bold text-neutral-900 text-sm uppercase mb-1">Terms & Notes</p>
                         <p className="text-sm text-neutral-600 leading-relaxed italic">{footerNote}</p>
                     </div>
                 </div>
             </div>
         </div>
       ) : (
         <div className="p-12 flex flex-col h-full">
            <div className="flex-1 creative-table-container">
                {children}
            </div>

            <div className="mt-auto pt-8">
                <div className="bg-neutral-50 p-6 border-l-4" style={{ borderColor: profile.brandColor }}>
                    <p className="font-bold text-neutral-900 text-sm uppercase mb-1">Terms & Notes</p>
                    <p className="text-sm text-neutral-600 leading-relaxed italic">{footerNote}</p>
                </div>
            </div>
         </div>
       )}
    </div>
  );
};

export default CreativeTemplate;
